import type { KeyboardEvent, SyntheticEvent } from 'react';
import { forwardRef, useMemo } from 'react';
import { useKeyboardActivation } from '../../hooks/useKeyboardActivation';
import { isNativeInteractiveElement } from '../../utils/isNativeInteractiveElement';
import type { BoxProps } from './types';

const Box = forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  const {
    as: Component = 'div',
    enableKeyboardActivation,
    onClick,
    onKeyDown: onKeyDownProp,
    tabIndex: tabIndexProp,
    ...rest
  } = props;

  const isKeyboardActivationRequested = Boolean(enableKeyboardActivation);
  const hasClickHandler = typeof onClick === 'function';
  const isNativeInteractive = isNativeInteractiveElement(Component);
  const keyboardActivationEnabled =
    isKeyboardActivationRequested && hasClickHandler && !isNativeInteractive;

  const { tabIndex, onKeyDown } = useKeyboardActivation({
    hasClickEvent: keyboardActivationEnabled,
    onClick: onClick as ((event: SyntheticEvent) => void) | undefined,
  });

  const composedOnKeyDown = useMemo(() => {
    if (onKeyDown && onKeyDownProp) {
      return (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyDownProp(event);
        if (!event.defaultPrevented) onKeyDown(event);
      };
    }
    return onKeyDown ?? onKeyDownProp;
  }, [onKeyDown, onKeyDownProp]);

  return (
    <Component
      {...rest}
      onClick={onClick}
      onKeyDown={composedOnKeyDown}
      ref={ref}
      tabIndex={tabIndex ?? tabIndexProp}
    />
  );
});

Box.displayName = 'Box';

export default Box;
